import type {
  AgentPresence,
  AgentPresencePayload,
  AlarmSignalInfo,
  CallIncomingPayload,
  CallStatePayload,
  IncomingCallInfo,
  SipConnectLoggingContext,
} from "~/services/calling/types";
import type { OperatorPresenceRecord, UserRecord } from "~/types/user";

const FIVE_SECOND_SIGNAL_PATTERNS = [
  "5-second",
  "5 second",
  "5sec",
  "5 sec",
  "five second",
  "check-in",
  "check in",
  "checkin",
];

const LOW_BATTERY_PATTERNS = [
  "low battery",
  "battery low",
  "battery-low",
  "battery_low",
];

const METADATA_LOW_BATTERY_KEYS = new Set([
  "lowbattery",
  "islowbattery",
  "batterylow",
]);

const METADATA_BATTERY_LEVEL_KEYS = new Set([
  "batterylevel",
  "batterypercentage",
  "batterypercent",
  "batterypct",
  "battery",
]);

const METADATA_REQUIRES_ANSWER_KEYS = new Set([
  "requiresanswer",
  "answerrequired",
  "needsanswer",
]);

const METADATA_INFORMATIONAL_KEYS = new Set([
  "isinformational",
  "informational",
  "isinfo",
  "infoonly",
  "noanswerrequired",
]);

const METADATA_ALARM_KEYS = new Set([
  "isalarm",
  "alarm",
  "issignal",
  "signal",
]);

function normalizeSignalText(value: string) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, " ");
}

function normalizeObjectKey(value: string) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function parseBooleanToken(value: unknown) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value === 1) return true;
    if (value === 0) return false;
    return undefined;
  }
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (["true", "yes", "y", "1", "on"].includes(normalized)) return true;
  if (["false", "no", "n", "0", "off"].includes(normalized)) return false;
  return undefined;
}

function parseNumberToken(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  if (!normalized) return undefined;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function readMetadataString(
  metadata: Record<string, unknown> | null | undefined,
  ...keys: string[]
) {
  if (!metadata) return undefined;
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string") {
      const normalized = value.trim();
      if (normalized) return normalized;
    }
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function walkMetadata(
  root: unknown,
  visitor: (key: string, value: unknown) => boolean | void
) {
  if (root == null) return;
  const queue: unknown[] = [root];
  const visited = new Set<unknown>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (current == null || typeof current !== "object") continue;
    if (visited.has(current)) continue;
    visited.add(current);

    if (Array.isArray(current)) {
      for (const item of current) {
        if (visitor("", item) === true) return;
        if (item && typeof item === "object") queue.push(item);
      }
      continue;
    }

    for (const [key, value] of Object.entries(current)) {
      if (visitor(key, value) === true) return;
      if (value && typeof value === "object") queue.push(value);
    }
  }
}

function findBooleanByMetadataKeys(
  root: unknown,
  targetKeys: ReadonlySet<string>
) {
  let found: boolean | undefined;
  walkMetadata(root, (key, value) => {
    if (!key) return;
    const normalizedKey = normalizeObjectKey(key);
    if (!targetKeys.has(normalizedKey)) return;
    const parsed = parseBooleanToken(value);
    if (parsed === undefined) return;
    found = parsed;
    return true;
  });
  return found;
}

function findNumberByMetadataKeys(
  root: unknown,
  targetKeys: ReadonlySet<string>
) {
  let found: number | undefined;
  walkMetadata(root, (key, value) => {
    if (!key) return;
    const normalizedKey = normalizeObjectKey(key);
    if (!targetKeys.has(normalizedKey)) return;
    const parsed = parseNumberToken(value);
    if (parsed === undefined) return;
    found = parsed;
    return true;
  });
  return found;
}

function includesPattern(haystack: string, patterns: readonly string[]) {
  if (!haystack) return false;
  return patterns.some((pattern) => haystack.includes(normalizeSignalText(pattern)));
}

function buildSignalText(payload: CallIncomingPayload) {
  const fragments = [
    payload.service,
    payload.description,
    payload.signalType,
  ]
    .filter((value): value is string => typeof value === "string")
    .map((value) => normalizeSignalText(value));
  if (payload.metadata) {
    try {
      fragments.push(normalizeSignalText(JSON.stringify(payload.metadata)));
    } catch {
      // Ignore metadata serialization failures.
    }
  }
  return fragments.join(" ");
}

export function isLowBatteryIncomingPayload(payload: CallIncomingPayload) {
  const explicitLowBattery = findBooleanByMetadataKeys(
    payload.metadata,
    METADATA_LOW_BATTERY_KEYS
  );
  if (explicitLowBattery === false) return false;
  if (explicitLowBattery === true) return true;

  const batteryLevel = findNumberByMetadataKeys(
    payload.metadata,
    METADATA_BATTERY_LEVEL_KEYS
  );
  if (typeof batteryLevel === "number" && batteryLevel <= 20) return true;

  const signalText = buildSignalText(payload);
  return includesPattern(signalText, LOW_BATTERY_PATTERNS);
}

export function isAlarmIncomingPayload(payload: CallIncomingPayload) {
  if (payload.requiresAnswer === false) return true;

  const answerRequired = findBooleanByMetadataKeys(
    payload.metadata,
    METADATA_REQUIRES_ANSWER_KEYS
  );
  if (answerRequired === false) return true;

  const informational = findBooleanByMetadataKeys(
    payload.metadata,
    METADATA_INFORMATIONAL_KEYS
  );
  if (informational === true) return true;

  const alarmFlag = findBooleanByMetadataKeys(payload.metadata, METADATA_ALARM_KEYS);
  if (alarmFlag === true) return true;

  if (isLowBatteryIncomingPayload(payload)) return true;

  const signalText = buildSignalText(payload);
  return includesPattern(signalText, FIVE_SECOND_SIGNAL_PATTERNS);
}

export function normalizeAgentStatus(value?: string | null) {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return "unknown";
  if (["online", "available"].includes(normalized)) return "online";
  if (["busy", "on-call", "in_call"].includes(normalized)) return "busy";
  if (["away", "break"].includes(normalized)) return "away";
  if (["offline", "disconnected"].includes(normalized)) return "offline";
  return "unknown";
}

export function normalizeAgent(payload: AgentPresencePayload): AgentPresence {
  return {
    id: payload.id,
    name: payload.name?.trim() || payload.id,
    status: normalizeAgentStatus(payload.status),
    sipExtension: payload.sipExtension ?? null,
    sipUri: payload.sipUri ?? null,
    profileImageUrl: payload.profileImageUrl ?? null,
  };
}

export function agentsAreEqual(
  left: AgentPresence[],
  right: AgentPresence[]
) {
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    const a = left[index];
    const b = right[index];
    if (
      a.id !== b.id ||
      a.name !== b.name ||
      a.status !== b.status ||
      a.sipExtension !== b.sipExtension ||
      a.sipUri !== b.sipUri ||
      a.profileImageUrl !== b.profileImageUrl
    ) {
      return false;
    }
  }

  return true;
}

/** Realtime agent feeds are considered stale after this long without an update. */
export const REALTIME_AGENTS_STALE_MS = 30_000;

export function areRealtimeAgentsFresh(
  updatedAtMs: number,
  nowMs: number = Date.now()
) {
  return updatedAtMs > 0 && nowMs - updatedAtMs < REALTIME_AGENTS_STALE_MS;
}

/**
 * Build an agent from live sidecar presence. Only `canTakeCalls` operators are marked online,
 * so transfer and conference targets can never point at an unregistered extension.
 */
export function normalizePresenceAgent(
  record: OperatorPresenceRecord,
  profile?: Pick<UserRecord, "profilePicUrl"> | null
): AgentPresence {
  const displayName = [record.name, record.surname]
    .filter(Boolean)
    .join(" ")
    .trim();
  return {
    id: record.userId,
    name: displayName || record.email,
    status: record.canTakeCalls ? "online" : "offline",
    sipExtension: record.extension ?? null,
    sipUri: null,
    profileImageUrl: profile?.profilePicUrl ?? null,
  };
}

export function getIncomingCallUniqueId(call: IncomingCallInfo) {
  return (
    call.uniqueId?.trim() ||
    call.callUuid?.trim() ||
    readMetadataString(call.metadata, "uniqueId", "unique_id", "callUuid", "call_uuid") ||
    null
  );
}

export function matchesCallEndedUniqueId(call: IncomingCallInfo, uniqueId: string) {
  const normalizedUniqueId = uniqueId.trim();
  if (!normalizedUniqueId) return false;
  const rowUniqueId = getIncomingCallUniqueId(call);
  if (rowUniqueId === normalizedUniqueId) return true;
  const rowCallId = call.callId?.trim();
  return Boolean(rowCallId && rowCallId === normalizedUniqueId);
}

export function normalizeIncomingCall(
  payload: CallIncomingPayload
): IncomingCallInfo {
  const metadata = isObjectRecord(payload.metadata) ? payload.metadata : null;
  const uniqueId =
    payload.callUuid ??
    readMetadataString(metadata, "uniqueId", "unique_id", "callUuid", "call_uuid") ??
    null;
  const callUuid = uniqueId;
  const callId =
    payload.callId ??
    readMetadataString(metadata, "callId", "call_id") ??
    null;
  const id = payload.id ?? uniqueId ?? callId ?? `call-${Date.now()}`;
  return {
    id,
    uniqueId,
    customerId: payload.customerId ?? null,
    callUuid,
    callId,
    callerNumber: payload.callerNumber ?? null,
    customerName: payload.customerName ?? null,
    service: payload.service ?? null,
    unitId:
      payload.unitId ??
      readMetadataString(
        metadata,
        "deviceUnitId",
        "device_unit_id",
        "unitId",
        "unit_id"
      ) ??
      null,
    description: payload.description ?? "Voice Call",
    signalType: payload.signalType ?? null,
    requiresAnswer:
      typeof payload.requiresAnswer === "boolean" ? payload.requiresAnswer : null,
    metadata,
    receivedAtMs: payload.receivedAtMs ?? Date.now(),
    deviceCid: payload.deviceCid ?? readMetadataString(metadata, "deviceCid", "device_cid") ?? null,
    ringGroup: payload.ringGroup ?? null,
  };
}

export function normalizeAlarmSignal(
  payload: CallIncomingPayload
): AlarmSignalInfo {
  const incoming = normalizeIncomingCall(payload);
  return {
    ...incoming,
    lowBattery: isLowBatteryIncomingPayload(payload),
    requiresAnswer: false,
  };
}

export function parseSipUser(value?: string | null) {
  if (!value) return null;
  const match = value.match(/^sip:([^@;]+)/i);
  return match ? match[1] : value;
}

export function normalizeSocketIdentityValue(value?: string | null) {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const fromSip = parseSipUser(trimmed);
  const normalized = (fromSip ?? trimmed).trim();
  if (!normalized) return null;
  const looksLikeDatabaseId =
    normalized.length >= 16 &&
    /[a-z]/i.test(normalized) &&
    /^[a-z0-9_-]+$/i.test(normalized);
  if (looksLikeDatabaseId) return null;
  return normalized;
}

export function collectIncomingCallCorrelators(call: IncomingCallInfo) {
  const values = [call.id, call.uniqueId, call.callUuid, call.callId];
  const correlators = new Set<string>();
  for (const value of values) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (trimmed) correlators.add(trimmed);
  }
  return [...correlators];
}

export function matchesQueueUniqueId(call: IncomingCallInfo, uniqueId: string) {
  const normalizedUniqueId = uniqueId.trim();
  if (!normalizedUniqueId) return false;
  return collectIncomingCallCorrelators(call).includes(normalizedUniqueId);
}

export function readUserSipExtension(user: unknown) {
  if (!user || typeof user !== "object") return null;
  const sipCredential = (user as Record<string, unknown>).sipCredential;
  if (!sipCredential || typeof sipCredential !== "object") return null;
  const extension = (sipCredential as Record<string, unknown>).extension;
  if (typeof extension === "string") return extension;
  if (typeof extension === "number" && Number.isFinite(extension)) {
    return String(extension);
  }
  return null;
}

export function buildSipIncomingFallback(
  fromUri: string | null
): IncomingCallInfo {
  const caller = parseSipUser(fromUri);
  return {
    id: `sip-${Date.now()}`,
    customerId: null,
    callUuid: null,
    callId: null,
    callerNumber: caller,
    customerName: null,
    service: "Incoming Call",
    description: "Voice Call",
    receivedAtMs: Date.now(),
  };
}

export function isNoPendingSipInviteError(error: unknown) {
  if (!(error instanceof Error)) return false;
  return /No incoming SIP invitation/i.test(error.message);
}

export function matchesIncomingCallIdentity(
  call: IncomingCallInfo,
  identity: { callUuid?: string; callId?: string; id?: string; uniqueId?: string }
) {
  if (identity.uniqueId && matchesCallEndedUniqueId(call, identity.uniqueId)) {
    return true;
  }
  if (identity.callUuid) {
    if (
      call.callUuid === identity.callUuid ||
      call.uniqueId === identity.callUuid ||
      call.id === identity.callUuid
    ) {
      return true;
    }
  }
  if (identity.callId) {
    if (call.callId === identity.callId || call.id === identity.callId) {
      return true;
    }
  }
  if (identity.id) {
    if (
      call.id === identity.id ||
      call.callUuid === identity.id ||
      call.callId === identity.id
    ) {
      return true;
    }
  }
  return false;
}

export function sanitizeOutboundTarget(value: string) {
  return value.trim().replace(/[\s()-]/g, "");
}

/**
 * The number sent to the phone system for an outbound call.
 *
 * SwitchTel only accepts South African numbers in national form, 0XXXXXXXXX. The call buttons
 * dial the stored number in international form, 27XXXXXXXXX. Live's phone server converts it
 * before dialling, but UAT's does not, so every button call failed there (AC-397). A number
 * written as +27… never reached SwitchTel on either, because the phone server's outbound route
 * only accepts digits. All of these are sent in national form here, matching the conversion on
 * live. Anything else is dialled as given: numbers already in national form, short codes such
 * as ER24's 084124, feature codes such as *43, extensions and foreign numbers.
 */
export function buildOutboundTarget(value: string) {
  const target = sanitizeOutboundTarget(value);
  const international = target.match(/^(?:\+|00)?27(\d{9})$/);
  return international ? `0${international[1]}` : target;
}

export function formatOutboundCallErrorMessage(
  error: unknown,
  target?: string | null
) {
  if (!(error instanceof Error) || !error.message) {
    return "Unable to place call.";
  }

  const sipError = error as { statusCode?: number; reasonPhrase?: string };
  const normalizedTarget = target?.trim() ?? "";
  const isEchoTest =
    normalizedTarget === "*43" || normalizedTarget === "%2A43";

  if (sipError.statusCode === 408 && isEchoTest) {
    return "Echo test (*43) timed out with no response from the PBX. Confirm *43 exists in your endpoint Asterisk context (see docs/asterisk-webrtc-sip-setup.md).";
  }

  if (sipError.statusCode != null && normalizedTarget) {
    const reason = sipError.reasonPhrase?.trim();
    const statusLabel = reason
      ? `${sipError.statusCode} ${reason}`
      : String(sipError.statusCode);
    return `Couldn't reach ${normalizedTarget} — line refused (${statusLabel})`;
  }

  if (/^SIP call rejected \(\d+/i.test(error.message) && normalizedTarget) {
    const match = error.message.match(/^SIP call rejected \((\d+)(?:\s+([^)]+))?\)/i);
    if (match) {
      const statusLabel = match[2]?.trim()
        ? `${match[1]} ${match[2].trim()}`
        : match[1];
      return `Couldn't reach ${normalizedTarget} — line refused (${statusLabel})`;
    }
  }

  if (/place the current call on hold before dialing out/i.test(error.message)) {
    return error.message;
  }

  if (/already in a call/i.test(error.message)) {
    return "You are already on a call. Place it on hold before dialing out.";
  }

  return error.message;
}

export function normalizeRealtimeEventName(eventName: string) {
  return eventName.trim().toLowerCase().replace(/[\s_.-]+/g, ":");
}

export function asEventObject(value: unknown) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function asEventString(value: unknown) {
  if (typeof value === "string") {
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
}

export function asEventNumber(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return undefined;
  const raw = value.trim();
  if (!raw) return undefined;
  const parsed = Number(raw);
  if (Number.isFinite(parsed)) return parsed;
  const parsedDate = Date.parse(raw);
  return Number.isFinite(parsedDate) ? parsedDate : undefined;
}

export function normalizeCallStateToken(value?: string | null) {
  if (!value) return "";
  return value.trim().toLowerCase().replace(/[^a-z]/g, "");
}

export function isIncomingTerminalCallState(value?: string | null) {
  const token = normalizeCallStateToken(value);
  return (
    token === "ended" ||
    token === "closed" ||
    token === "hangup" ||
    token === "missed" ||
    token === "cancelled" ||
    token === "canceled" ||
    token === "rejected" ||
    token === "declined" ||
    token === "noanswer" ||
    token === "timeout"
  );
}

export function sanitizeHistoryKeyPart(value?: string | null) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "");
}

export function buildIncomingTerminalHistoryKey(
  payload: Pick<CallStatePayload, "callUuid" | "callId" | "id" | "state">,
  incoming?: IncomingCallInfo
) {
  const payloadKey = sanitizeHistoryKeyPart(
    payload.callUuid ?? payload.callId ?? payload.id ?? null
  );
  const incomingKey = sanitizeHistoryKeyPart(
    incoming?.callUuid ??
      incoming?.callId ??
      incoming?.id ??
      incoming?.callerNumber ??
      null
  );
  const baseKey = payloadKey || incomingKey;
  if (!baseKey) return "";
  return baseKey.slice(0, 80);
}

export function parseAgentIdentity(value: unknown) {
  const objectValue = asEventObject(value);
  if (!objectValue) {
    return {
      id: undefined,
      name: undefined,
    };
  }
  const idValue = objectValue.id ?? objectValue.agentId ?? objectValue.agent_id;
  const nameValue =
    objectValue.name ??
    objectValue.agentName ??
    objectValue.agent_name ??
    objectValue.displayName ??
    objectValue.display_name;
  return {
    id:
      typeof idValue === "string"
        ? idValue
        : typeof idValue === "number"
          ? String(idValue)
          : undefined,
    name:
      typeof nameValue === "string"
        ? nameValue
        : typeof nameValue === "number"
          ? String(nameValue)
          : undefined,
  };
}

export function buildEventMessage(value: unknown, fallback: string) {
  if (typeof value === "string" && value.trim().length > 0) return value;
  const objectValue = asEventObject(value);
  if (!objectValue) return fallback;
  const messageValue =
    objectValue.message ??
    objectValue.description ??
    objectValue.reason ??
    objectValue.event;
  if (typeof messageValue === "string" && messageValue.trim().length > 0) {
    return messageValue;
  }
  return fallback;
}

/** Display name for the current operator (e.g. for call history). */
export function getOperatorDisplayName(user: {
  name?: string | null;
  surname?: string | null;
  email?: string | null;
  extensionId?: string | null;
} | null): string | null {
  if (!user) return null;
  const name = [user.name, user.surname].filter(Boolean).join(" ").trim();
  return name || (user.email ?? user.extensionId ?? null);
}

/** Throws if SIP is not registered. Use at the start of call/answer/transfer/etc. */
export function requireSipRegistered(
  state: {
    sipStatus?: string;
    socketStatus?: string;
    lastError?: string | null;
    lastSipDisconnectReason?: string | null;
    sipDomain?: string | null;
  },
  /** From SipPhone.getConnectLoggingContext() (includes SIP password). */
  connect: SipConnectLoggingContext | null = null,
  actualRegistrationState?: string | null
): void {
  const stackRegistered = actualRegistrationState === "registered";
  if (stackRegistered) return;

  if (
    state.sipStatus === "registered" &&
    (actualRegistrationState == null || actualRegistrationState === "registered")
  ) {
    return;
  }

  const snapshot = {
    sipStatus: state.sipStatus ?? null,
    actualRegistrationState: actualRegistrationState ?? null,
    socketStatus: state.socketStatus ?? null,
    lastError: state.lastError ?? null,
    lastSipDisconnectReason: state.lastSipDisconnectReason ?? null,
    stateSipDomain: state.sipDomain ?? null,
    connect,
  };
  const connectOneLine = connect
    ? `wss=${connect.sipWssUrl} connectSipDomain=${connect.sipDomain} sipUser=${connect.username} authUser=${connect.authUsername ?? "—"} credDomain=${connect.credentialDomain ?? "—"} displayName=${connect.displayName ?? "—"} sipPassword=${connect.password}`
    : `connectSnapshot=(none) stateSipDomain=${state.sipDomain ?? "—"}`;
  // Use console.error so this shows when DevTools is filtered to Errors only (warn is often hidden).
  console.error(
    `[SIP] Phone is not registered; operation blocked. sipStatus=${String(snapshot.sipStatus)} socketStatus=${String(snapshot.socketStatus)} lastError=${snapshot.lastError ?? "—"} lastSipDisconnect=${snapshot.lastSipDisconnectReason ?? "—"} ${connectOneLine}`,
    snapshot
  );
  throw new Error("Phone is not registered.");
}
